import { Link } from "react-router-dom";
import { Search, TrendingUp, Target, ArrowRight } from "lucide-react";

const GoogleShopping = () => {
  return (
    <div className="min-h-screen">
      <section className="bg-gray-50 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h1 className="text-5xl font-bold mb-6">
              <span className="gradient-text">Google Shopping</span>
              <br />
              Optimization
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Maximize your Google Shopping performance with AI-powered bid
              management, competitor analysis, and product optimization.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="bg-white rounded-2xl shadow-xl p-8">
                <h3 className="text-lg font-semibold mb-6">
                  Google Shopping Dashboard
                </h3>
                <div className="space-y-4">
                  <div className="flex justify-between items-center p-3 bg-green-50 rounded-lg">
                    <span className="text-gray-700">Product A</span>
                    <div className="text-right">
                      <div className="font-semibold text-green-600">
                        Position #1
                      </div>
                      <div className="text-xs text-gray-500">$0.85 CPC</div>
                    </div>
                  </div>
                  <div className="flex justify-between items-center p-3 bg-yellow-50 rounded-lg">
                    <span className="text-gray-700">Product B</span>
                    <div className="text-right">
                      <div className="font-semibold text-yellow-600">
                        Position #3
                      </div>
                      <div className="text-xs text-gray-500">$1.20 CPC</div>
                    </div>
                  </div>
                  <div className="flex justify-between items-center p-3 bg-blue-50 rounded-lg">
                    <span className="text-gray-700">Product C</span>
                    <div className="text-right">
                      <div className="font-semibold text-blue-600">
                        Position #2
                      </div>
                      <div className="text-xs text-gray-500">$0.95 CPC</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="space-y-6">
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-orange-500 rounded-lg flex items-center justify-center">
                  <Search className="h-6 w-6 text-white" />
                </div>
                <div>
                  <h3 className="text-xl font-semibold mb-2">
                    Smart Bid Management
                  </h3>
                  <p className="text-gray-600">
                    AI-powered bidding strategies to maximize ROI and improve ad
                    positions.
                  </p>
                </div>
              </div>
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-blue-600 rounded-lg flex items-center justify-center">
                  <TrendingUp className="h-6 w-6 text-white" />
                </div>
                <div>
                  <h3 className="text-xl font-semibold mb-2">
                    Performance Analytics
                  </h3>
                  <p className="text-gray-600">
                    Deep insights into click-through rates, conversion rates,
                    and competitor performance.
                  </p>
                </div>
              </div>
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-green-600 rounded-lg flex items-center justify-center">
                  <Target className="h-6 w-6 text-white" />
                </div>
                <div>
                  <h3 className="text-xl font-semibold mb-2">
                    Product Optimization
                  </h3>
                  <p className="text-gray-600">
                    Optimize product titles, descriptions, and images for better
                    visibility.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-4">
              <span className="gradient-text">Key Features</span>
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                title: "Automated Bidding",
                description:
                  "AI adjusts bids in real-time based on performance data",
                icon: "🤖",
              },
              {
                title: "Competitor Analysis",
                description:
                  "Track competitor ads, pricing, and positioning strategies",
                icon: "🔍",
              },
              {
                title: "Feed Optimization",
                description:
                  "Optimize product feeds for maximum visibility and clicks",
                icon: "📊",
              },
              {
                title: "Negative Keywords",
                description:
                  "Automatically identify and add negative keywords to reduce waste",
                icon: "🚫",
              },
              {
                title: "Campaign Management",
                description:
                  "Streamlined campaign creation and management tools",
                icon: "⚙️",
              },
              {
                title: "ROI Tracking",
                description:
                  "Comprehensive ROI and ROAS tracking across all campaigns",
                icon: "💰",
              },
            ].map((feature, index) => (
              <div key={index} className="card">
                <div className="text-4xl mb-4">{feature.icon}</div>
                <h3 className="text-xl font-semibold mb-3">{feature.title}</h3>
                <p className="text-gray-600">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 gradient-bg">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold text-white mb-6">
            Dominate Google Shopping
          </h2>
          <p className="text-xl text-orange-100 mb-8">
            Increase visibility, reduce costs, and maximize ROI with intelligent
            Google Shopping optimization
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/trial"
              className="bg-white text-gray-900 px-8 py-4 rounded-lg font-semibold hover:bg-gray-100 transition-colors inline-flex items-center justify-center"
            >
              Start Free Trial <ArrowRight className="ml-2 h-5 w-5" />
            </Link>
            <Link
              to="/contact"
              className="border-2 border-white text-white px-8 py-4 rounded-lg font-semibold hover:bg-white hover:text-gray-900 transition-colors"
            >
              Schedule Demo
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default GoogleShopping;
